import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useRef, useState } from "react";
import {
  chatInvestigationLink,
  investigationContent,
  type ChatInvestigation,
  type MonitoringInvestigation,
  type MonitoringInvestigationContext,
} from "../../../shared/chatInvestigation";
import { sharedMutators } from "../../../shared/mutators";
import { usePersonalWorkEnabled } from "../../hooks/usePersonalWorkEnabled";
import { navigate, routes } from "../../lib/urls";
import { stageChatInvestigation } from "../PersonalChats/useChatInvestigation";
import { Icon } from "../Icon";
import {
  MapChromeClickButton,
  MapChromeCorner,
  MapChromePill,
  mapChromeSlideUp,
} from "../MapChrome";
import { Text } from "../Text";
import { emitErrorToast } from "../../lib/errorToast";
import { classifyZeroResult, type ZeroResult } from "../../lib/zeroResultError";
import { Tooltip } from "../Tooltip";
import { useMonitoringInteraction } from "./analytics";
import { useMonitoringScope } from "./useMonitoringScope";

const visibleActionStyles = `
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
  animation: var(--investigation-entrance);
`;

export const monitoringInvestigationHover = `
  &:hover [data-investigation-action],
  &:focus-within [data-investigation-action] {
    ${visibleActionStyles}
  }
`;

const ActionPill = styled(MapChromePill)`
  --investigation-entrance: ${mapChromeSlideUp} var(--motion-slow)
    var(--ease-overshoot);
  &[data-busy="true"] {
    ${visibleActionStyles}
  }
  @media (hover: none) {
    --investigation-entrance: none;
    ${visibleActionStyles}
  }
`;

const Action = styled(MapChromeClickButton)`
  width: auto;
  padding: 0 8px 0 6px;
  gap: 5px;
  white-space: nowrap;
`;

export function AskMonitoringChat({
  surface,
  snapshot,
  label = "Ask in Chat",
  context,
  disabled = false,
}: {
  surface: MonitoringInvestigation;
  snapshot: string;
  label?: "Ask in Chat" | "Explore in Chat" | "Investigate in Chat";
  context?: MonitoringInvestigationContext;
  disabled?: boolean;
}) {
  const z = useZero();
  const enabled = usePersonalWorkEnabled();
  const { scope } = useMonitoringScope(routes.monitoring);
  const recordInteraction = useMonitoringInteraction();
  const creatingRef = useRef(false);
  const [creating, setCreating] = useState(false);
  if (!enabled) return null;

  const openChat = async () => {
    if (creatingRef.current || disabled) return;
    creatingRef.current = true;
    setCreating(true);
    const id = nanoid(12);
    const chatId = nanoid(12);
    const sourcePath = `${location.pathname}${location.search}`;
    const investigation: ChatInvestigation = {
      ...context,
      source: "monitoring",
      surface,
      scope: context?.scope ?? scope,
      capturedAt: Date.now(),
      sourcePath,
      snapshot,
    };
    const onError = (result: ZeroResult) => {
      creatingRef.current = false;
      setCreating(false);
      const decision = classifyZeroResult(result);
      if (decision.kind === "toast")
        emitErrorToast({
          icon: "circle-off",
          message:
            decision.message ?? "The chat couldn’t be opened. Try again.",
          buttonLabel: "Dismiss",
        });
    };
    try {
      const result = await z.mutate(
        sharedMutators.createPersonalChat({
          id,
          chatId,
          name: investigationContent[surface].sourceTitle,
        })
      ).server;
      if (result.type === "error") {
        onError(result);
        return;
      }
      recordInteraction("investigation.open", {
        value: surface,
        targetType: "personal_chat",
        targetId: id,
      });
      stageChatInvestigation(id, investigation);
      navigate(routes.chats, {
        tabId: id,
        monitoring: chatInvestigationLink(investigation),
      });
    } catch (error) {
      onError({ type: "error", error });
    }
  };

  return (
    <MapChromeCorner data-side="right">
      <ActionPill data-investigation-action data-busy={creating}>
        <Tooltip
          content={investigationContent[surface].tooltip}
          contentStyle={{ maxWidth: 200, whiteSpace: "pre-line" }}
        >
          <Action
            data-no-pill
            aria-label={`${label}: ${investigationContent[surface].sourceTitle}`}
            aria-busy={creating}
            disabled={creating || disabled}
            onClick={openChat}
          >
            <Icon name="ask" size={12} />
            <Text variant="text-small-md" color="inherit">
              {label}
            </Text>
          </Action>
        </Tooltip>
      </ActionPill>
    </MapChromeCorner>
  );
}
